import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function TermsAndConditions() {
  const lastUpdated = "April 7, 2026";

  const termsContent = [
    {
      id: "1",
      title: "1. Glossary",
      content: "In these Terms and Conditions, certain capitalized words have specific meanings. 'Platform' refers to our website and services. 'User', 'you', and 'your' refer to you, the person accessing the platform. 'We', 'us', and 'our' refer to the company providing the service."
    },
    {
      id: "2",
      title: "2. User Agreement",
      content: "By registering an account or accessing the platform, you enter into a legally binding agreement with us. We reserve the right to modify these terms at any time. Your continued use of the platform following any changes indicates your acceptance of the new terms."
    },
    {
      id: "3",
      title: "3. Your License",
      content: "We grant you a personal, non-exclusive, non-transferable, and revocable license to access and use our courses and materials strictly for your personal, non-commercial education. You may not copy, distribute, modify, or create derivative works from our content without explicit permission."
    },
    {
      id: "4",
      title: "4. Support Services",
      content: "We provide support services to assist you with technical issues and general inquiries regarding the platform. Support is provided 'as is' and we do not guarantee specific response times, though we strive to assist all users promptly."
    },
    {
      id: "5",
      title: "5. Fees and Payment",
      content: "Access to certain premium courses requires payment of fees. All fees are stated upfront and are non-refundable unless otherwise explicitly stated in our refund policy. We use secure third-party payment processors to handle your transactions safely."
    },
    {
      id: "6",
      title: "6. Cancellation",
      content: "You may cancel your subscription at any time through your account settings. Cancellation will prevent future billing, but you will retain access to the platform until the end of your current paid billing cycle."
    },
    {
      id: "7",
      title: "7. Rights & Termination",
      content: "We reserve the right to terminate or suspend your account and access to the platform immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms."
    },
    {
      id: "8",
      title: "8. Your Responsibilities",
      content: "You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account. You also agree not to use the platform for any illegal or unauthorized purpose."
    },
    {
      id: "9",
      title: "9. Suspension/Discontinuation",
      content: "We reserve the right to modify, suspend, or discontinue the platform (or any part or content thereof) at any time with or without notice to you. We shall not be liable to you or to any third party for any modification or suspension."
    },
    {
      id: "10",
      title: "10. Copyright",
      content: "All content included on the platform, such as text, graphics, logos, images, and software, is our property or the property of our content suppliers and protected by international copyright laws."
    },
    {
      id: "11",
      title: "11. Links to Other Websites",
      content: "Our platform may contain links to third-party websites or services that are not owned or controlled by us. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites."
    },
    {
      id: "12",
      title: "12. Trademarks",
      content: "Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent. All other trademarks not owned by us that appear on this site are the property of their respective owners."
    },
    {
      id: "13",
      title: "13. Force Majeure",
      content: "We shall not be held liable for any failure or delay in performance of our obligations under these Terms if such failure or delay is caused by circumstances beyond our reasonable control, including acts of God, natural disasters, or internet outages."
    },
    {
      id: "14",
      title: "14. Disclaimer of Warranties",
      content: "The platform is provided on an 'AS IS' and 'AS AVAILABLE' basis. We make no representations or warranties of any kind, express or implied, as to the operation of the platform or the information, content, or materials included on it."
    },
    {
      id: "15",
      title: "15. Age Restriction",
      content: "You must be at least 18 years of age to create an account and use this platform. By using the platform, you warrant that you meet this age requirement."
    },
    {
      id: "16",
      title: "16. General Provisions",
      content: "These Terms constitute the entire agreement between you and us regarding the use of the platform. If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will remain in full force and effect."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen font-sans text-white bg-[#0F172A]  relative ">

      {/* GLOBAL BACKGROUND GLOWS */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1612550761236-e813928f7271?auto=format&fit=crop&q=80&w=2400"
          alt="Background"
          className="w-full h-full object-cover opacity-[0.03] mix-blend-screen"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 via-[#0F172A]/95 to-[#0F172A]" />

        {/* Soft Theme Glows */}
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-[#2563EB]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-[#F43F5E]/10 rounded-full blur-[150px]" />
      </div>

      {/* HEADER */}
      <div className="relative z-50">
        <Header />
      </div>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 w-full max-w-5xl mx-auto px-4  pt-40 pb-16  relative z-10 py-16 md:py-24 md:pt-40">

        {/* The Glassmorphism Document Card */}
        <div className="w-full bg-[#1E293B]/80 backdrop-blur-2xl rounded-[2.5rem] border border-slate-700/80 shadow-[0_0_50px_rgba(0,0,0,0.3)] overflow-hidden relative">

          {/* Top highlight bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2563EB] via-purple-500 to-[#F43F5E] opacity-80" />

          {/* Document Header */}
          <div className="px-8 pt-16 pb-8 md:px-16 md:pt-20 md:pb-12 border-b border-slate-700/80">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
              Terms and <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">Conditions</span>
            </h1>
            <p className="text-slate-400 font-medium">
              Last Updated: <span className="text-[#2563EB]">{lastUpdated}</span>
            </p>
          </div>

          {/* Document Body */}
          <div className="px-8 py-12 md:px-16 md:py-16 space-y-12">

            {/* Introduction block */}
            <div className="space-y-4">
              <p className="text-slate-300 leading-relaxed text-lg">
                Please read these terms and conditions carefully before using our platform. By accessing or using the service, you agree to be bound by these terms. If you disagree with any part of the terms, then you may not access the service.
              </p>
              <p className="text-slate-300 leading-relaxed text-lg">
                These Terms and Conditions shall be governed by and construed in accordance with the laws of our operating jurisdiction, without regard to its conflict of law provisions.
              </p>
            </div>

            {/* Render all sections from the array */}
            {termsContent.map((section) => (
              <section key={section.id} className="space-y-4 scroll-mt-24" id={`section-${section.id}`}>
                <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-3">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">
                    {section.title.split('.')[0]}.
                  </span>
                  {section.title.split('.')[1]}
                </h2>
                <p className="text-slate-300 leading-relaxed">
                  {section.content}
                </p>
              </section>
            ))}

          </div>

      

        </div>

      </main>

      {/* FOOTER */}
      <div className="relative mt-auto">
        <Footer />
      </div>
    </div>
  );
}